import { reactive } from 'vue'
import { cookie } from '@/utils/cookie'
import { notify } from 'notiwind'
import { translate } from '@/utils/translate'
export function useCartStore () {
  const state = reactive({
    cart: [],
    total: {},
    isLoading: false,
    error: null,
    contactRequired: false,
    shippingRequired: false,
    pendingItemUpdate: null
  })

  async function fetchCartContent () {
    state.isLoading = true
    state.error = null

    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/content`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            brand_id: window.vueAppConfig.info.id,
            session_id: cookie()
          })
        }
      )

      const data = await response.json()
      if (data.status) {
        state.cart = data.items
        state.total = data.total
        if (data.shipping_required) {
          state.shippingRequired = true
        }
      } else {
        state.error = translate('Failed to load cart content')
      }
    } catch (err) {
      state.error = translate('Error fetching cart content')
    } finally {
      state.isLoading = false
    }
  }

  const updateQuantity = async (itemId, quantity, contact = null) => {
    state.isLoading = true
    state.error = null

    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/item/update-quantity`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            item_id: itemId,
            quantity: quantity,
            brand_id: window.vueAppConfig.info.id,
            contact: contact,
            session_id: cookie()
          })
        }
      )

      const data = await response.json()

      if (data.status) {
        await fetchCartContent()
        state.contactRequired = false
        state.pendingItemUpdate = null
      } else if (data.contact_required) {
        state.contactRequired = true
        state.pendingItemUpdate = { itemId, quantity }
      } else {
        if (data.message) {
          notify(
            {
              title: translate('Error'),
              text: data.message,
              type: 'error',
              group: 'error'
            },
            15000
          )
        } else {
          state.error = translate('Failed to update quantity')
        }
      }
    } catch (error) {
      state.error = 'Error updating quantity'
    } finally {
      state.isLoading = false
    }
  }

  const addAddon = async (itemId, addon = null) => {
    state.isLoading = true
    state.error = null

    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/item/add-addon`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            item_id: itemId,
            addon_id: addon,
            brand_id: window.vueAppConfig.info.id,
            session_id: cookie()
          })
        }
      )

      const data = await response.json()

      if (data.status) {
        await fetchCartContent()
        state.contactRequired = false
        state.pendingItemUpdate = null
      } else {
        state.error = 'Failed to add addon'
      }
    } catch (error) {
      state.error = 'Error adding addon'
    } finally {
      state.isLoading = false
    }
  }
  const removeItem = async (itemId, type) => {
    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/item/remove-item`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            item_id: itemId,
            type: type,
            brand_id: window.vueAppConfig.info.id,
            session_id: cookie()
          })
        }
      )

      const data = await response.json()
      if (data.status) {
        await fetchCartContent()
      } else {
        state.error = 'Failed to remove item'
      }
    } catch (err) {
      state.error = 'Error removing item'
    }
  }

  const removeContact = async (itemId, contactId) => {
    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/item/remove-contact`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            item_id: itemId,
            contact_id: contactId,
            brand_id: window.vueAppConfig.info.id,
            session_id: cookie()
          })
        }
      )

      const data = await response.json()
      if (data.status) {
        await fetchCartContent()
      } else {
        state.error = 'Failed to remove contact'
      }
    } catch (err) {
      state.error = 'Error removing contact'
    }
  }

  const removeVoucher = async (itemId, voucherId) => {
    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/item/remove-voucher`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            item_id: itemId,
            brand_id: window.vueAppConfig.info.id,
            voucher_id: voucherId,
            session_id: cookie()
          })
        }
      )

      const data = await response.json()
      if (data.status) {
        await fetchCartContent()
      } else {
        state.error = 'Failed to remove voucher'
      }
    } catch (err) {
      state.error = 'Error removing voucher'
    }
  }

  const removePackage = async itemId => {
    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/item/remove-package`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            item_id: itemId,
            brand_id: window.vueAppConfig.info.id,
            session_id: cookie()
          })
        }
      )

      const data = await response.json()
      if (data.status) {
        await fetchCartContent()
      } else {
        state.error = 'Failed to remove package'
      }
    } catch (err) {
      state.error = 'Error removing package'
    }
  }
  const addItemToCart = async (type, product, showError = true) => {
    const sessionId = cookie()
    const id = product.id

    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/cart/add`, // No parameters in the URL
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: id,
            brand_id: window.vueAppConfig.info.id,
            type: type,
            session_id: sessionId
          }) // Move the parameters to the body
        }
      )

      const data = await response.json()

      if (data.status) {
        await fetchCartContent(window.vueAppConfig.info.id)
        return true
      } else if (showError) {
        notify(
          {
            title: translate('Error'),
            text: data.message ?? translate('Something went wrong'),
            type: 'error',
            group: 'error'
          },
          15000
        )
        return false
      }
    } catch (error) {
      console.error('Error adding to cart:', error)
      return false
    }
  }

  return {
    state,
    addItemToCart,
    fetchCartContent,
    updateQuantity,
    addAddon,
    removeItem,
    removeContact,
    removeVoucher,
    removePackage
  }
}
